import {Button, Icon, Popup} from '@startlibs/components'
import {useToggle, usePopupToggle} from '@startlibs/core'
import {FormContext, useFormValue} from '@startlibs/form'
import {getColor} from '@startlibs/utils'
import {darken, desaturate, transparentize} from 'polished'
import React, {useContext, useState} from 'react'
import styled, {css} from 'styled-components'
import {FormattedMessage, useIntl} from 'react-intl'
import {Card} from '../../components/PageLayout'
import {setNotification} from '../../components/Notifications'
import {lazyUserInfo} from '../../components/WithProvider'
import {REVIEWER_TYPE_ADMIN, REVIEWER_TYPE_EXPERT} from '../../enums/UserRole'
import {getJwt} from '../../hooks/useJwt'
import {
  MAX_EXPERT_TIME_MINUTES,
  deleteExpertTime,
  isOwnExpertTimeEntry,
  saveExpertTime,
  sumExpertTimes
} from './expertTimeUtils'
import {getExpertTimeErrorMessage} from './ExpertTime'

const ERROR_COLOR = '#c3282d'

// Light tint of the main color, same derivation used by ReleaseLocationCard.
const tint = (alpha, darkenBy = 0.012, color = 'main') => (props) => transparentize(alpha, darken(darkenBy, desaturate(0.4, getColor(color)(props))))

const ListCard = styled(Card)`
  position: relative;
  padding: 1.5rem 2.5rem;
  ${props => props.hasError && css`border: 1px solid ${ERROR_COLOR};`}
`

const Rows = styled.div`
  ${props => props.table && css`
    border-radius: 6px;
    box-shadow: 0 0 0 1px ${tint(0.7)};
    > div:nth-child(odd) {
      background: ${tint(0.93)};
    }
    > div:first-child {
      border-radius: 6px 6px 0 0;
    }
    > div:last-child {
      border-radius: 0 0 6px 6px;
    }
    > div + div {
      border-top: 1px solid ${tint(0.8)};
    }
  `}
`

const TintButton = styled(Button)`
  box-shadow: none;
  color: ${getColor('main')};
  background: ${tint(0.9)};
  :hover {
    background: ${tint(0.8)};
  }
  :active {
    background: ${tint(0.7)};
  }
  ${props => props.pressed && css`
    color: white;
    background: ${props => darken(0.08, getColor('main')(props))};
    box-shadow: 0 0 0 3px ${tint(0.7)};
    :hover {
      background: ${props => darken(0.08, getColor('main')(props))};
    }
  `}
`

const DeleteAnchor = styled.div`
  position: relative;
`

const DeletePopup = styled(({className, name, isLoading, hasError, onCancel, onConfirm}) =>
  <Popup arrow my="bottom center" at="top center" className={className} onClick={(e) => e.stopPropagation()}>
    <header>
      <h4><FormattedMessage defaultMessage="Delete time?" description="Expert time spent delete dialog title"/></h4>
      <Icon icon="x" onClick={onCancel}/>
    </header>
    <p><FormattedMessage
      defaultMessage="Remove the time logged by {name}?"
      description="Expert time spent delete dialog message"
      values={{name}}
    /></p>
    {hasError && <p className="error"><FormattedMessage
      defaultMessage="Total time spent on case could not be deleted."
      description="Expert time spent delete error"
    /></p>}
    <footer>
      <Button type="button" onClick={onCancel}><FormattedMessage defaultMessage="Cancel" description="Cancel Button"/></Button>
      <Button type="button" alert icon="delete" isLoading={isLoading} onClick={onConfirm}>
        <FormattedMessage defaultMessage="Delete" description="Expert time spent delete button"/>
      </Button>
    </footer>
  </Popup>
)`
  z-index: 10;
  min-width: 21rem;
  padding: 1.5rem;
  border-radius: 10px;
  background: white;
  color: ${getColor('gray60')};
  font-weight: normal;
  text-align: left;
  cursor: default;
  header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    ${Icon} {
      cursor: pointer;
      font-size: 20px;
      color: ${getColor('gray150')};
    }
  }
  h4 {
    margin: 0 0 1rem;
  }
  p {
    margin: 0 0 1.5rem;
    font-size: 14px;
  }
  .error {
    color: ${ERROR_COLOR};
  }
  footer {
    display: flex;
    justify-content: flex-end;
  }
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  min-height: 4.5rem;
  padding: 0.75rem 1.25rem;
  .person {
    display: flex;
    align-items: center;
    flex: 0 0 15rem;
    min-width: 0;
    color: ${getColor('gray60')};
    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
  .avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 2.75rem;
    width: 2.75rem;
    height: 2.75rem;
    margin-right: 0.75rem;
    overflow: hidden;
    border: 1px solid ${getColor('gray120')};
    border-radius: 50%;
    color: ${getColor('gray120')};
    ${Icon} {
      font-size: 1.5rem;
    }
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
  .time {
    flex: 1;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0 1rem;
  }
  .actions {
    display: flex;
    gap: 0.75rem;
  }
  ${props => props.hasError && css`
    box-shadow: inset 0 0 0 1px ${ERROR_COLOR};
  `}
`

const Minutes = styled.span`
  color: ${getColor('gray60')};
  strong {
    font-size: 18px;
    margin-right: 0.25rem;
  }
  small {
    color: ${getColor('gray150')};
    font-size: 12px;
  }
`

const TotalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  padding: 1rem 1.25rem 0;
  .label {
    flex: 0 0 15rem;
    padding-left: 3rem;
    color: ${getColor('gray150')};
  }
`

const ErrorText = styled.p`
  flex-basis: 100%;
  color: ${ERROR_COLOR};
  margin: 0;
`

const InputBox = styled.div`
  display: flex;
  align-items: center;
  height: 3rem;
  width: 6.5rem;
  padding: 0 0.5rem 0 1rem;
  background: white;
  border: 1px solid ${props => props.hasError ? ERROR_COLOR : getColor('gray210')};
  border-radius: 8px;
  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    font-size: 16px;
    color: ${getColor('gray60')};
    &::placeholder {
      color: ${getColor('gray180')};
    }
  }
  &:focus-within {
    border-color: ${getColor('main')};
  }
  .arrows {
    display: flex;
    flex-direction: column;
  }
  .arrows button {
    border: 0;
    background: transparent;
    padding: 0.125rem 0.25rem;
    color: ${getColor('main')};
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    &:disabled {
      color: ${getColor('gray180')};
      cursor: default;
    }
  }
`

// Controlled by the total in minutes (null when empty).
const MinutesInput = ({value, onChange, hasError, autoFocus, onEnter, onEscape}) => {
  const intl = useIntl()
  const step = (delta) => onChange(Math.min(Math.max((value ?? 0) + delta, 0), MAX_EXPERT_TIME_MINUTES))

  const onInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '')
    onChange(digits === '' ? null : Math.min(Number(digits), MAX_EXPERT_TIME_MINUTES))
  }

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      step(1)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      step(-1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      onEnter?.()
    } else if (e.key === 'Escape') {
      onEscape?.()
    }
  }

  return <InputBox hasError={hasError}>
    <input
      type="text"
      inputMode="numeric"
      placeholder="00"
      aria-label={intl.formatMessage({defaultMessage: 'minutes', description: 'Expert time spent minutes label'})}
      autoFocus={autoFocus}
      maxLength={String(MAX_EXPERT_TIME_MINUTES).length}
      value={value ?? ''}
      onChange={onInputChange}
      onKeyDown={onKeyDown}
    />
    <div className="arrows">
      <button type="button" tabIndex={-1} disabled={value >= MAX_EXPERT_TIME_MINUTES} onClick={() => step(1)}><Icon icon="arrow-up"/></button>
      <button type="button" tabIndex={-1} disabled={!value} onClick={() => step(-1)}><Icon icon="arrow-down"/></button>
    </div>
  </InputBox>
}

const MinutesValue = ({minutes}) => <Minutes>
  <strong>{minutes ?? '--'}</strong>
  <small><FormattedMessage defaultMessage="min" description="Expert time spent minutes abbreviation"/></small>
</Minutes>

// Falls back to the placeholder icon when the reviewer has no picture.
const Person = ({label, reviewer}) => {
  const [failed, setFailed] = useState(false)
  const src = reviewer?.reviewerId && !failed
    ? `/api/${reviewer.reviewerType === REVIEWER_TYPE_EXPERT ? 'expert' : 'admin'}/${reviewer.reviewerId}/picture?t=${getJwt()}&no-cache=${reviewer.picture ?? ''}`
    : null

  return <div className="person">
    <div className="avatar">
      {src ? <img src={src} alt="" onError={() => setFailed(true)}/> : <Icon icon="physician"/>}
    </div>
    <span>{label}</span>
  </div>
}

const ExpertTimeRow = ({requestId, entry, label, readOnly, required, onChange}) => {
  const intl = useIntl()
  const editing = useToggle()
  const saving = useToggle()
  const [draft, setDraft] = useState(null)
  const [error, setError] = useState()

  const startEditing = () => {
    setDraft(entry.minutes ?? null)
    setError(undefined)
    editing.open()
  }

  const changeDraft = (minutes) => {
    setDraft(minutes)
    setError(undefined)
  }

  const save = () => {
    const message = getExpertTimeErrorMessage(intl, draft, required)
    if (message) return setError(message)
    if (draft === null || draft === entry.minutes) return editing.close()
    return saving.wait(saveExpertTime(requestId, draft, entry)
      .then(() => {
        onChange(entry, {minutes: draft})
        editing.close()
      })
      .catch(() => setNotification({
        type: 'alert',
        msg: () => <FormattedMessage
          defaultMessage="Total time spent on case could not be saved."
          description="Expert time spent save error"
        />
      }))
    )
  }

  const deletePopup = usePopupToggle()
  const deleting = useToggle()
  const [deleteFailed, setDeleteFailed] = useState(false)

  const closeDeletePopup = () => {
    setDeleteFailed(false)
    deletePopup.close()
  }

  const remove = () => deleting.wait(deleteExpertTime(requestId, entry)
    .then(() => {
      closeDeletePopup()
      onChange(entry, {minutes: null})
    })
    .catch(() => setDeleteFailed(true))
  )

  const canEdit = !readOnly && entry.canEdit

  return <Row hasError={!!error}>
    <Person label={label} reviewer={entry}/>
    <div className="time">
      {editing.isOpen
        ? <MinutesInput value={draft} onChange={changeDraft} hasError={!!error} autoFocus onEnter={save} onEscape={editing.close}/>
        : <MinutesValue minutes={entry.minutes}/>
      }
    </div>
    {canEdit && (editing.isOpen
      ? <div className="actions">
        <TintButton type="button" small icon="x" onClick={editing.close}><FormattedMessage defaultMessage="Cancel" description="Cancel Button"/></TintButton>
        <Button type="button" small highlight icon="check" isLoading={saving.isOpen} onClick={save}>
          <FormattedMessage defaultMessage="Save" description="Expert time spent save button"/>
        </Button>
      </div>
      : <div className="actions">
        <TintButton type="button" small icon="edit" onClick={startEditing}><FormattedMessage defaultMessage="Edit" description="Expert time spent edit button"/></TintButton>
        {entry.minutes !== null && entry.minutes !== undefined &&
          <DeleteAnchor>
            <Button type="button" small alert icon="delete" onClick={deletePopup.open}>
              <FormattedMessage defaultMessage="Delete" description="Expert time spent delete button"/>
            </Button>
            {deletePopup.isOpen &&
              <DeletePopup name={label} isLoading={deleting.isOpen} hasError={deleteFailed} onCancel={closeDeletePopup} onConfirm={remove}/>
            }
          </DeleteAnchor>
        }
      </div>
    )}
    {error && <ErrorText>{error}</ErrorText>}
  </Row>
}

// `youInput` ({value, onChange, hasError}) turns the logged user's row into an input owned by the caller (a form field).
export const AdminExpertTime = ({requestId, entries, readOnly, showEmpty, hasError, required, onEntryChange, youInput}) => {
  const intl = useIntl()
  const userInfo = lazyUserInfo.read()
  const ownName = `${userInfo?.firstName ?? ''} ${userInfo?.lastName ?? ''}`.trim()
  const youLabel = (name) => name
    ? intl.formatMessage({defaultMessage: '{name} (you)', description: 'Expert time spent label for the logged user, with the name'}, {name})
    : intl.formatMessage({defaultMessage: 'You', description: 'Expert time spent label for the logged user'})

  let rows = entries.map((entry) => ({entry, isYou: isOwnExpertTimeEntry(entry, userInfo), key: `${entry.reviewerType}-${entry.reviewerId}`}))
  if (youInput && !rows.some((row) => row.isYou)) {
    rows = [...rows, {entry: {minutes: null}, isYou: true, key: 'you'}]
  }
  if (readOnly && !showEmpty) {
    rows = rows.filter(({entry}) => entry.minutes !== null && entry.minutes !== undefined)
  }
  if (!rows.length) return null

  const isTable = rows.length > 1
  const total = sumExpertTimes(rows.map(({entry, isYou}) => isYou && youInput ? youInput.value : entry.minutes))

  return <ListCard noPrint hasError={hasError || youInput?.hasError}>
    <Rows table={isTable}>
      {rows.map(({entry, isYou, key}) => isYou && youInput
        ? <Row key={key} hasError={youInput.hasError}>
          <Person label={youLabel(ownName)} reviewer={{reviewerType: REVIEWER_TYPE_ADMIN, reviewerId: userInfo?.id}}/>
          <div className="time">
            <MinutesInput value={youInput.value} onChange={youInput.onChange} hasError={youInput.hasError}/>
          </div>
        </Row>
        : <ExpertTimeRow
          key={key}
          requestId={requestId}
          entry={entry}
          label={isYou ? youLabel(entry.name || ownName) : entry.name}
          readOnly={readOnly}
          required={required}
          onChange={onEntryChange}
        />
      )}
    </Rows>
    {isTable && <TotalRow>
      <span className="label"><FormattedMessage defaultMessage="Total time:" description="Expert time spent total label"/></span>
      <MinutesValue minutes={total}/>
    </TotalRow>}
  </ListCard>
}

// Inside a WithForm: the logged user's time lives on the form path and is sent with the review payload.
export const AdminExpertTimeForm = ({path, hasError, ...props}) => {
  const form = useContext(FormContext)
  const [value] = useFormValue(path)

  const onChange = (minutes) => {
    form.setValue(path, minutes)
    form.removeErrorPath(path)
  }

  return <AdminExpertTime {...props} youInput={{value: value ?? null, onChange, hasError}}/>
}
